import React, { useEffect, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';

import Seo from '../components/Seo';
import { getResults, calculateProfile, getResultDrivers } from '../utils/profileCalculator';
import { founderTypes } from '../data/founderTypes';
import { typePlaybooks } from '../data/typePlaybooks';
import { formatFounderTitle } from '../utils/textUtils';
import { decodeResultsShareParam } from '../utils/shareLinkUtils';
import { calculateArchetypes } from '../utils/archetypeCalculator';
import { getProfileDimensions, getCofounderPrompts } from '../utils/profileDimensions';
import { buildTypeCode, buildIdentityHeadline } from '../utils/founderIdentity';
import ResultDriversList from '../components/ResultDriversList';
import TypeIcon from '../components/TypeIcon';

const PrintStyles = createGlobalStyle`
  @media print {
    body {
      background: white !important;
      color: #0f172a !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    @page {
      margin: 12mm 14mm;
      size: A4;
    }
    .no-print {
      display: none !important;
    }
    .page-break {
      break-after: page;
      page-break-after: always;
    }
  }
`;

const Page = styled.div`
  min-height: 100vh;
  background: #ffffff;
  color: #0f172a;
  font-family: 'Inter', system-ui, sans-serif;
  padding: 1.25rem;
`;

const Container = styled.div`
  max-width: 780px;
  margin: 0 auto;
`;

const Cover = styled.section`
  min-height: 92vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.5rem 0 2rem;
  border-bottom: 3px solid ${(props) => props.$accent || '#2541b2'};

  @media print {
    min-height: 248mm;
  }
`;

const CoverTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const Brand = styled.div`
  font-family: 'Nunito', 'Inter', sans-serif;
  font-weight: 800;
  font-size: 1.15rem;
  letter-spacing: -0.02em;
  color: #0f172a;
`;

const CoverMeta = styled.div`
  text-align: right;
  font-size: 0.88rem;
  color: #64748b;
  line-height: 1.5;
`;

const CoverHero = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2.5rem 0 2rem;
`;

const TypeBadge = styled.span`
  display: inline-block;
  font-family: 'Nunito', sans-serif;
  font-weight: 800;
  font-size: 0.82rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${(props) => props.$accent || '#2541b2'};
  margin-bottom: 1rem;
`;

const CoverIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1rem;
  color: ${(props) => props.$accent || '#2541b2'};
`;

const CoverTitle = styled.h1`
  font-family: 'Nunito', 'Inter', sans-serif;
  font-size: 2.75rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin: 0 0 0.75rem;
  color: #0f172a;
  max-width: 16ch;
`;

const CoverSubtitle = styled.p`
  font-size: 1.15rem;
  line-height: 1.55;
  color: #334155;
  margin: 0 0 1.5rem;
  max-width: 42ch;
`;

const ScoreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const ScorePill = styled.div`
  padding: 0.65rem 1rem;
  border-radius: 999px;
  background: ${(props) => `${props.$accent}14` || 'rgba(37, 65, 178, 0.08)'};
  border: 1px solid ${(props) => `${props.$accent}33` || 'rgba(37, 65, 178, 0.2)'};
  font-size: 0.92rem;
  font-weight: 600;
  color: #1e293b;
`;

const CoverFooter = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
`;

const DocTitle = styled.h2`
  font-family: 'Nunito', 'Inter', sans-serif;
  font-size: 1.35rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 0.35rem;
  color: #0f172a;
`;

const DocLead = styled.p`
  margin: 0 0 1.25rem;
  color: #475569;
  line-height: 1.55;
  font-size: 0.98rem;
`;

const Card = styled.section`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 1.1rem 1.15rem;
  margin-bottom: 0.85rem;
  break-inside: avoid;
  page-break-inside: avoid;
`;

const SectionTitle = styled.h3`
  font-family: 'Nunito', 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
  color: #0f172a;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid ${(props) => props.$accent || '#2541b2'};
  display: inline-block;
`;

const List = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1.2rem;
  color: #1e293b;
  line-height: 1.55;
  font-size: 0.95rem;
`;

const OrderedList = styled.ol`
  margin: 0.5rem 0 0;
  padding-left: 1.2rem;
  color: #1e293b;
  line-height: 1.55;
  font-size: 0.95rem;
`;

const BodyText = styled.p`
  margin: 0;
  color: #334155;
  line-height: 1.55;
  font-size: 0.95rem;
`;

const Quote = styled.blockquote`
  margin: 0.5rem 0 0;
  padding: 0.65rem 0 0.65rem 0.85rem;
  border-left: 3px solid ${(props) => props.$accent || '#2541b2'};
  color: #475569;
  font-style: italic;
  line-height: 1.55;
  font-size: 0.92rem;
  background: #f8fafc;
  border-radius: 0 6px 6px 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const Button = styled.button`
  background: #0f172a;
  color: white;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #2541b2;
    outline-offset: 2px;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #2541b2;
    outline-offset: 2px;
  }
`;

const Footer = styled.p`
  margin-top: 1.5rem;
  font-size: 0.82rem;
  color: #64748b;
  text-align: center;
  line-height: 1.5;
`;

const ResultsPrint = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resultsData = getResults();
  const profileFromStorage = resultsData?.profile || null;
  const storedAnswers = useMemo(
    () => (Array.isArray(resultsData?.answers) ? resultsData.answers : []),
    [resultsData?.answers]
  );

  const profile = useMemo(() => {
    if (profileFromStorage?.primaryType) {
      const next = { ...profileFromStorage };
      if (storedAnswers.length > 0 && !Array.isArray(next.drivers)) {
        const recalculated = calculateProfile(storedAnswers);
        next.drivers = recalculated.drivers;
        next.confidence = next.confidence || recalculated.confidence;
      }
      return next;
    }

    const shareParam = new URLSearchParams(location.search).get('share');
    const shared = decodeResultsShareParam(shareParam);
    if (!(shared?.p && shared?.pct)) return null;

    const normalizedPrimary = String(shared.p).toLowerCase();

    return {
      primaryType: normalizedPrimary,
      secondaryType: shared.s ? String(shared.s).toLowerCase() : null,
      percentages: shared.pct,
      confidence: shared.conf || { label: 'low', marginPercent: 0 },
      scoringVersion: shared.sv || 1,
      compatibleTypes: founderTypes[normalizedPrimary]?.compatibleWith || [],
      archetypes: calculateArchetypes(shared.pct),
    };
  }, [profileFromStorage, storedAnswers, location.search]);

  useEffect(() => {
    if (!profile || !profile.primaryType) {
      navigate('/', { replace: true });
    }
  }, [navigate, profile]);

  if (!profile || !profile.primaryType) return null;

  const primary = founderTypes[profile.primaryType];
  const secondary = profile.secondaryType ? founderTypes[profile.secondaryType] : null;
  const playbook = typePlaybooks[profile.primaryType];
  const dimensions = getProfileDimensions(profile.percentages);
  const cofounderPrompts = getCofounderPrompts(profile, founderTypes);
  const createdAt = resultsData?.createdAt ? new Date(resultsData.createdAt) : new Date();
  const primaryDrivers = getResultDrivers(profile, { limit: 5, typeFilter: profile.primaryType });
  const topDrivers = getResultDrivers(profile, { limit: 5 });
  const accent = primary?.color || '#2541b2';
  const typeCode = buildTypeCode(profile.primaryType, profile.secondaryType);
  const identityHeadline = buildIdentityHeadline(profile);

  return (
    <Page id="main-content">
      <Seo
        title="Export"
        description="Printable FounderDNA results"
        path="/results/print"
        imagePath="/favicon.svg"
      />
      <PrintStyles />
      <Container>
        <Cover className="page-break" $accent={accent} aria-labelledby="cover-title">
          <CoverTop>
            <Brand>FounderDNA</Brand>
            <CoverMeta>
              {createdAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
              <br />
              myfounderdna.com
            </CoverMeta>
          </CoverTop>

          <CoverHero>
            {typeCode && <TypeBadge $accent={accent}>{typeCode}</TypeBadge>}
            {primary && (
              <CoverIcon $accent={accent} aria-hidden="true">
                <TypeIcon type={primary} size={56} color={accent} />
              </CoverIcon>
            )}
            <CoverTitle id="cover-title">{identityHeadline}</CoverTitle>
            <CoverSubtitle>{primary?.tagline || 'Your founder operating profile'}</CoverSubtitle>
            <ScoreRow>
              <ScorePill $accent={accent}>
                Primary: {formatFounderTitle(primary?.title || profile.primaryType)} (
                {profile.percentages?.[profile.primaryType] ?? 0}%)
              </ScorePill>
              {secondary && (
                <ScorePill $accent={secondary.color}>
                  Secondary: {formatFounderTitle(secondary.title)} (
                  {profile.percentages?.[profile.secondaryType] ?? 0}%)
                </ScorePill>
              )}
            </ScoreRow>
          </CoverHero>

          <CoverFooter>
            Informational self-assessment only. Not clinical, hiring, or investment advice. Results stored locally
            unless you choose to share them.
          </CoverFooter>
        </Cover>

        <DocTitle>Report details</DocTitle>
        <DocLead>
          A forward-ready snapshot of how you tend to lead, decide, and execute. Share with a co-founder or advisor.
        </DocLead>

        <Card>
          <SectionTitle $accent={accent}>Summary</SectionTitle>
          <List>
            <li>
              Confidence: {profile?.confidence?.label || 'low'} (margin {profile?.confidence?.marginPercent ?? 0}%)
            </li>
            <li>Scoring version: {profile?.scoringVersion || 1}</li>
          </List>
        </Card>

        {primaryDrivers.length > 0 && (
          <Card>
            <SectionTitle $accent={accent}>Answers that shaped your primary type</SectionTitle>
            <BodyText style={{ marginBottom: '0.75rem' }}>
              Weighted choices that most strongly pointed to {formatFounderTitle(primary?.title || 'your primary type')}.
            </BodyText>
            <ResultDriversList
              drivers={primaryDrivers}
              primaryType={profile.primaryType}
              variant="print"
              highlightPrimary
            />
          </Card>
        )}

        {topDrivers.length > 0 && (
          <Card>
            <SectionTitle $accent={accent}>Top weighted answers (all types)</SectionTitle>
            <BodyText style={{ marginBottom: '0.75rem' }}>
              Your five highest-signal answers across all founder modes.
            </BodyText>
            <ResultDriversList
              drivers={topDrivers}
              primaryType={profile.primaryType}
              variant="print"
              highlightPrimary
            />
          </Card>
        )}

        {profile?.archetypes?.primary && (
          <Card>
            <SectionTitle $accent={accent}>Archetypes</SectionTitle>
            <List>
              <li>
                Primary: {profile.archetypes.primary.title} ({profile.archetypes.primary.score}%)
              </li>
              {profile?.archetypes?.secondary && (
                <li>
                  Secondary: {profile.archetypes.secondary.title} ({profile.archetypes.secondary.score}%)
                </li>
              )}
            </List>
            {profile.archetypes.primary.focus && (
              <BodyText style={{ marginTop: '0.75rem' }}>Focus: {profile.archetypes.primary.focus}</BodyText>
            )}
          </Card>
        )}

        <Card>
          <SectionTitle $accent={accent}>Operating dimensions</SectionTitle>
          <List>
            {dimensions.map((dim) => (
              <li key={dim.id}>
                {dim.label}: {dim.value}%
              </li>
            ))}
          </List>
        </Card>

        {playbook?.stressFailureMode && (
          <Card>
            <SectionTitle $accent={accent}>Default failure mode under stress</SectionTitle>
            <BodyText>{playbook.stressFailureMode}</BodyText>
            {playbook.decisionChecklist?.length > 0 && (
              <>
                <BodyText style={{ marginTop: '0.75rem', fontWeight: 600 }}>Decision checklist</BodyText>
                <List>
                  {playbook.decisionChecklist.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </List>
              </>
            )}
          </Card>
        )}

        {playbook?.threeStepWeekPlan?.length > 0 && (
          <Card>
            <SectionTitle $accent={accent}>Your 3-step plan for this week</SectionTitle>
            <OrderedList>
              {playbook.threeStepWeekPlan.map((item) => (
                <li key={item.title} style={{ marginBottom: '0.75rem' }}>
                  <strong>{item.title}</strong>
                  {item.steps?.length > 0 && (
                    <List>
                      {item.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </List>
                  )}
                </li>
              ))}
            </OrderedList>
          </Card>
        )}

        {playbook?.meetingScripts?.length > 0 && (
          <Card>
            <SectionTitle $accent={accent}>Meeting scripts</SectionTitle>
            {playbook.meetingScripts.map((item) => (
              <div key={item.title} style={{ marginTop: '0.75rem' }}>
                <strong>{item.title}</strong>
                <Quote $accent={accent}>{item.script}</Quote>
              </div>
            ))}
          </Card>
        )}

        {cofounderPrompts.length > 0 && (
          <Card>
            <SectionTitle $accent={accent}>Co-founder conversation starters</SectionTitle>
            {cofounderPrompts.map((item) => (
              <div key={item.title} style={{ marginTop: '0.75rem' }}>
                <strong>{item.title}</strong>
                <Quote $accent={accent}>{item.prompt}</Quote>
              </div>
            ))}
          </Card>
        )}

        {profile.compatibleTypes?.length > 0 && (
          <Card>
            <SectionTitle $accent={accent}>Compatible co-founder types</SectionTitle>
            <List>
              {profile.compatibleTypes.map((typeKey) => {
                const type = founderTypes[typeKey?.toLowerCase()];
                return type ? (
                  <li key={typeKey}>
                    {formatFounderTitle(type.title)}: {type.strengths[0]}
                  </li>
                ) : null;
              })}
            </List>
          </Card>
        )}

        {primary?.actionPlan && (
          <Card>
            <SectionTitle $accent={accent}>Development plan</SectionTitle>
            <BodyText>{primary.actionPlan}</BodyText>
          </Card>
        )}

        <Card className="no-print">
          <SectionTitle $accent={accent}>Export</SectionTitle>
          <BodyText>
            Click Print and choose Save as PDF to share with a co-founder or advisor.
          </BodyText>
          <ButtonRow>
            <Button type="button" onClick={() => window.print()}>
              Print or save PDF
            </Button>
            <SecondaryButton type="button" onClick={() => navigate('/results')}>
              Back to results
            </SecondaryButton>
          </ButtonRow>
        </Card>

        <Footer>
          FounderDNA · Informational only · Not a clinical or hiring assessment ·{' '}
          <a href="/privacy" style={{ color: '#64748b' }}>Privacy</a>
          {' · '}
          <a href="/terms" style={{ color: '#64748b' }}>Terms</a>
        </Footer>
      </Container>
    </Page>
  );
};

export default ResultsPrint;
